import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";

import CountUp from "react-countup";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import isEqual from "lodash/isEqual";
import isEmpty from "lodash/isEmpty";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import ProjectService from "../../../services/projectService";

import * as projectActions from "../../../actions/projectActions";

import Loader from "../../common/Loader";

import TimesheetProgressBarFill from "./TimesheetProgressBarFill";

import { getProjectClassName } from "../../project/ProjectProgressBar";

// Helper function to convert duration based on units
const convertDuration = (duration, units) => {
  if (units === "Minutes") {
    return duration / 60; // Convert minutes to hours
  }
  if (units === "Hours") {
    return duration;
  }
};

const checkForTaskChanges = (originalTasks = [], updatedTasks = []) => {
  // Create maps for efficient lookup
  const originalMap = new Map(originalTasks.map((task) => [task._id, task]));
  const updatedMap = new Map(updatedTasks.map((task) => [task._id, task]));

  // Initialize variables to track updated, created, and deleted items
  const updatedItems = [];
  const createdItems = [];
  const deletedItems = [];

  // Recompute the sum of actual_duration
  let sumActualDuration = 0;

  // Iterate through originalArray to find updated and deleted items
  originalTasks.forEach((originalItem) => {
    const updatedItem = updatedMap.get(originalItem._id);

    if (updatedItem) {
      // Check for updates
      if (!isEqual(originalItem, updatedItem)) {
        updatedItems.push(updatedItem);
      }
      sumActualDuration += convertDuration(
        parseFloat(updatedItem.actual_duration || 0),
        updatedItem.duration_units
      );
    } else {
      // Item was deleted
      deletedItems.push(originalItem);
    }
  });

  // Iterate through updatedArray to find created items
  updatedTasks.forEach((updatedItem) => {
    if (!originalMap.has(updatedItem._id)) {
      // Item is new
      createdItems.push(updatedItem);
      sumActualDuration += convertDuration(
        parseFloat(updatedItem.actual_duration || 0),
        updatedItem.duration_units
      );
    }
  });

  return sumActualDuration;
};

const TimesheetProgressBarDisplay = ({
  call,
  budgetHours,
  budgetConsumed,
  updatedDuration,
  project,
  auth,
  isBudgetSegmet
}) => {
  if (isEmpty(project)) {
    return <Loader loading />;
  }

  let actual_hrs = call.actual_hrs || 0;
  // if project changes, the call's actual hours from the selected project is 0
  if (call.project && call.project._id && call.project._id !== project._id) {
    actual_hrs = 0;
  }

  const projectConsumedWithoutCall = budgetConsumed.totalHours - actual_hrs;

  const remaining =
    budgetHours - (projectConsumedWithoutCall + updatedDuration);

  const consumedPercentage = budgetHours
    ? parseFloat(projectConsumedWithoutCall / budgetHours) * 100
    : parseFloat(
        projectConsumedWithoutCall /
          (projectConsumedWithoutCall + updatedDuration)
      ) * 100;

  const currentCallPercentage = budgetHours
    ? parseFloat(updatedDuration / budgetHours) * 100
    : parseFloat(
        updatedDuration / (projectConsumedWithoutCall + updatedDuration)
      ) * 100;

  const classNames = budgetHours
    ? getProjectClassName(consumedPercentage + currentCallPercentage)
    : "bg-gravoc";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 15
      }}
    >
      <div
        style={{
          flexGrow: 1,
          fontFamily: "Nunito"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 16
          }}
        >
          <div>
            <b>{project.name}</b>
          </div>
          <div>
            <b>
              {budgetHours ? (
                <CountUp
                  className="counter"
                  suffix={
                    remaining >= 0 ? ` HRS REMAINING` : ` HRS OVER BUDGET`
                  }
                  decimals={2}
                  separator=","
                  end={Math.abs(remaining)}
                />
              ) : (
                <CountUp
                  className="counter"
                  suffix={` HRS CONSUMED`}
                  decimals={2}
                  separator=","
                  end={Math.abs(remaining)}
                />
              )}
            </b>
          </div>
        </div>
        <TimesheetProgressBarFill
          auth={auth}
          height={10}
          values={[
            {
              classNames,
              percentage: consumedPercentage,
              callId: call._id
            },
            {
              classNames,
              percentage: currentCallPercentage,
              callId: call._id
            }
          ]}
          projectConsumedWithoutCall={projectConsumedWithoutCall}
          updatedDuration={updatedDuration}
        />
        <div>
          <b>{(projectConsumedWithoutCall + updatedDuration).toFixed(2)}</b>
          {` OUT OF `}
          <b>{budgetHours.toFixed(2)}</b>
          {` HRS ${isBudgetSegmet ? " THIS MONTH" : ""}`}
        </div>
      </div>
    </div>
  );
};

TimesheetProgressBarDisplay.propTypes = {
  budgetHours: PropTypes.number,
  budgetConsumed: PropTypes.object,
  updatedDuration: PropTypes.number,
  call: PropTypes.object,
  project: PropTypes.object,
  auth: PropTypes.object,
  isBudgetSegmet: PropTypes.bool
};

const TimesheetProgressBar = ({
  projectId,
  project,
  call,
  tasks,
  dates,
  auth,
  actions,
  clearProjectOnUnmount
}) => {
  const [budgetConsumed, setBudgetConsumed] = useState({
    totalHours: 0,
    totalBillableHours: 0
  });

  useEffect(() => {
    const fetchBudgetConsumed = async () => {
      if (projectId) {
        actions.getProject(projectId);
        const result = await ProjectService.getBudgetConsumed(projectId);
        if (result?.[0]) {
          setBudgetConsumed(result[0]);
        }
      }
    };
    fetchBudgetConsumed();

    return () => {
      if (clearProjectOnUnmount) {
        actions.clearProject();
      }
    };
  }, [actions, projectId, clearProjectOnUnmount]);

  const callDate = useMemo(() => {
    const { arrival_date, day } = call;
    if (arrival_date) {
      return new Date(arrival_date);
    }
    if (day && dates?.length) {
      const dateFound = dates.find((date) => date.day === call.day);
      if (dateFound?.date) {
        return new Date(dateFound.date);
      }
    }
    return new Date();
  }, [call, dates]);

  const currentMonthlySegment = useMemo(
    () =>
      project.budget_segments?.find(
        (segment) =>
          callDate >= startOfDay(new Date(segment.startDate)) &&
          callDate <= endOfDay(new Date(segment.endDate))
      ),
    [project, callDate]
  );

  const updatedDuration = useMemo(
    () => checkForTaskChanges(call.tasks, tasks) || 0,
    [call, tasks]
  );

  if (currentMonthlySegment) {
    return (
      <TimesheetProgressBarDisplay
        auth={auth}
        budgetHours={currentMonthlySegment.budgetHours}
        budgetConsumed={{
          ...currentMonthlySegment,
          totalHours: currentMonthlySegment.consumed
        }}
        updatedDuration={updatedDuration || 0}
        call={call}
        isBudgetSegmet
        project={project}
      />
    );
  }

  return (
    <TimesheetProgressBarDisplay
      auth={auth}
      budgetHours={project.budget_hours || 0}
      budgetConsumed={budgetConsumed}
      updatedDuration={updatedDuration || 0}
      call={call}
      isBudgetSegmet={false}
      project={project}
    />
  );
};

TimesheetProgressBar.propTypes = {
  projectId: PropTypes.string,
  project: PropTypes.object,
  call: PropTypes.object,
  tasks: PropTypes.array,
  dates: PropTypes.array,
  auth: PropTypes.object,
  actions: PropTypes.object,
  clearProjectOnUnmount: PropTypes.bool
};

TimesheetProgressBar.defaultProps = {
  project: {},
  call: {},
  dates: []
};

function mapStatesToProps(state) {
  return {
    project: state.reducers.project
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...projectActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(TimesheetProgressBar);
